import { getDatePermisB, isFormationBloquante } from '@workspace/utils';
import { useWatch } from 'react-hook-form';
import { OUI_NON_STRING_OPTIONS } from './options';
import { Question } from '../../question';
import { FieldRadioGroup } from '../../smart-form';
import { useAutoNext } from '../use-auto-next';
import { CarteGrise, Warning } from '../../../icons-amo';
import { BulleAide } from '../../bulle-aide';

/*
 * Contenus de questions (à placer dans un <Question> de l'app). Portage de legacy `@amo/core/components/questions`
 * DeclarationFormation et DeclarationAssurance (Attestation125 de l'avenant ; conditions d'affichage :
 * `getAttestation125Questions` de @workspace/utils).
 */

const PERSONNE = 'DemandeTarif.ListePersonnes.0';
export const FORMATION = `${PERSONNE}.Formation`;
export const DECLARATION_ASSURANCE = `${PERSONNE}.DeclarationAssurance`;

function AlerteConditions125({ nbCasDispense }: { nbCasDispense: 2 | 3 }) {
  return (
    <div role="alert" className="w-full">
      <BulleAide icon={<Warning />} title="Attention !" className="border border-error-500 bg-error-50 text-error-900">
        <p>
          <b>Vous pouvez conduire une moto 125cm³ avec un permis B si vous remplissez les 2 conditions suivantes :</b>
          <br />
          - Vous avez le permis B depuis au moins 2 ans
          <br />
          - Vous avez suivi une formation pratique de 7 heures
          <br />
          <br />
          <b>
            Vous pouvez conduire un scooter 3 roues de la catégorie L5e avec un permis B si vous remplissez les 3 conditions
            suivantes :
          </b>
          <br />
          - Vous avez le permis B depuis au moins 2 ans
          <br />
          - Vous avez 21 ans ou plus
          <br />
          - Vous avez suivi une formation pratique de 7 heures
          <br />
          <br />
          {/* why: iso legacy, « 2 cas » dans DeclarationFormation et « 3 cas » dans DeclarationAssurance pour la même liste */}
          <b>Vous êtes dispensé de suivre la formation dans les {nbCasDispense} cas suivants :</b>
          <br />
          - Vous avez obtenu votre permis B (avec l&apos;équivalence A1) avant mars 1980
          <br />
          - Vous avez conduit une 125cm³ ou un scooter 3 roues de la catégorie L5e entre 2006 et 2010
          <br />- Si vous avez suivi la formation de 3 heures avant 2011
        </p>
      </BulleAide>
    </div>
  );
}

/** Formation de 7 h ? « Non » est bloquant si le permis B date d'après le 01/01/2011. */
export function DeclarationFormationContent() {
  const { ref, next } = useAutoNext();
  const formation = useWatch({ name: FORMATION });
  const listePermis = useWatch({ name: `${PERSONNE}.ListePermis` });
  const bloquant = isFormationBloquante(getDatePermisB(listePermis), formation);

  return (
    <div ref={ref} className="w-full flex flex-col items-center space-y-4">
      <Question.Header>
        <CarteGrise />
        <Question.Title>
          Vous déclarez avoir réalisé <b>la formation de 7h</b> pour conduire ce <b>type de véhicule</b> ?
        </Question.Title>
      </Question.Header>
      <FieldRadioGroup
        name={FORMATION}
        options={OUI_NON_STRING_OPTIONS}
        onClick={next}
        validation={{
          // false (sans message) : le détail est affiché par l'alerte ci-dessous
          validate: (value: unknown, values: Record<string, any>) =>
            !isFormationBloquante(getDatePermisB(values?.DemandeTarif?.ListePersonnes?.[0]?.ListePermis), value),
        }}
      />
      {bloquant && <AlerteConditions125 nbCasDispense={2} />}
    </div>
  );
}

/** Assuré 125 / L5e entre 2006 et 2010 ? « Non » est bloquant. */
export function DeclarationAssuranceContent() {
  const { ref, next } = useAutoNext();
  const declaration = useWatch({ name: DECLARATION_ASSURANCE });

  return (
    <div ref={ref} className="w-full flex flex-col items-center space-y-4">
      <Question.Header>
        <CarteGrise />
        <Question.Title>
          Vous déclarez avoir été assuré de{' '}
          <b>manière permanente ou non pour un 2 roues 125cm³ ou un 3 roues de la catégorie L5e entre 2006 et 2010</b> ?
        </Question.Title>
      </Question.Header>
      <FieldRadioGroup
        name={DECLARATION_ASSURANCE}
        options={OUI_NON_STRING_OPTIONS}
        onClick={next}
        validation={{ validate: (value: unknown) => value !== '0' }}
      />
      {declaration === '0' && <AlerteConditions125 nbCasDispense={3} />}
    </div>
  );
}
